import Auth from '@aws-amplify/auth'
import { useSession } from '@emerald-works/react-auth'
import { useConnectionContext } from '@emerald-works/react-event-bus-client'
import Gleap from 'gleap'
import { useCallback } from 'react'
import { ADS_VARIANT_STORAGE_KEY } from '../components/google-ad/google-ad-variant'
import { isEmbedded } from '../helpers/embedded-cookies'

const shouldRemove = (cookie) => !/cookieyes|fallback|dd|_dd_s|cky-consent|cky-action|euconsent|Cognito/.test(cookie)

/**
 * Suffix that expires a cookie. Inside the Learnforce iframe (PENG-3889) the
 * session cookies are written SameSite=None; Secure; Partitioned, and a cookie is
 * only expired by a write whose attributes match — without these the logout would
 * leave the embedded session in place.
 */
const expirySuffix = () =>
  isEmbedded()
    ? 'max-age=-1;expires=Thu, 01 Jan 1970 00:00:00 GMT;SameSite=None;Secure;Partitioned'
    : 'max-age=-1;expires=Thu, 01 Jan 1970 00:00:00 GMT'

const clearNovaCurrentSessionCookie = () => {
  const cookieDomain = window.location.hostname.split('.').slice(1).join('.')
  if (!cookieDomain) return

  document.cookie = `Nova.CurrentSession=;domain=${cookieDomain};path=/;${expirySuffix()}`
  document.cookie = `Nova.CurrentSession=;domain=.${cookieDomain};path=/;${expirySuffix()}`
}

const useLogout = () => {
  const session = useSession()
  const connection = useConnectionContext()

  const logout = useCallback(
    async path => {
      const deleteCookies = () => {
        const cookies = document.cookie.split(/; */)

        cookies.forEach(cookie => {
          const [name] = cookie.split('=')

          if (shouldRemove(name)) {
            document.cookie = `${name}=;path=/;${expirySuffix()}`
            document.cookie = `${name}=;path=${location.pathname};${expirySuffix()}`
          }

          // Attempt to delete cookies set with subdomains and paths
          const domainParts = location.hostname.split('.')
          while (domainParts.length >= 1) {
            if (shouldRemove(name)) {
              document.cookie = `${name}=;domain=${domainParts.join('.')};path=/;${expirySuffix()}`
              document.cookie = `${name}=;domain=${domainParts.join('.')};path=${location.pathname};${expirySuffix()}`
            }
            domainParts.shift()
          }
        })
      }

      // Keep ADS_VARIANT_STORAGE_KEY in localStorage
      const clearLocalStorage = () => {
        const storedAdsVariant = localStorage.getItem(ADS_VARIANT_STORAGE_KEY)
        localStorage.clear()
        if (storedAdsVariant) {
          localStorage.setItem(ADS_VARIANT_STORAGE_KEY, storedAdsVariant)
        }
      }

      const reload = async () => {
        try {
          await session.updateSessionContext()
          await connection.reloadConnection()
          window.location.href = new URL(path, window.location.href)
        } catch (err) {
          window.location.href = new URL(path, window.location.href)
        }
      }
      if (window.DD_RUM && typeof window.DD_RUM.clearUser === 'function') {
        window.DD_RUM.clearUser()
      } else {
        console.log('clearUser is not available')
      }
      if (window.DD_RUM && typeof window.DD_RUM.clearGlobalContext === 'function') {
        window.DD_RUM.clearGlobalContext()
      } else {
        console.log('clearGlobalContext is not available')
      }
      Gleap.clearIdentity()

      // Clear client state before Cognito OAuth sign-out, which redirects the browser
      // to /signout and skips the remaining cleanup below for hosted UI users.
      clearNovaCurrentSessionCookie()
      deleteCookies()
      clearLocalStorage()

      try {
        await Auth.signOut()
      } catch (error) {
        // OAuth sign-out redirects away and rejects after a timeout.
        if (error?.message !== 'Signout timeout fail') {
          throw error
        }
        return
      }

      await reload()
    },
    [session, connection]
  )

  return { logout }
}

export default useLogout
